/**
 * The session-stack state machine shared by the floating chat surfaces — the
 * canvas provider (`CanvasFloatingChatContext`) and the home/workspace provider
 * (`WorkspaceFloatingChatContext`) drive the same window from this so the two
 * can't drift:
 *
 *  - **Session navigation**: an in-memory stack (push/replace/back).
 *    `currentSessionId` is the top of the stack (or null).
 *  - **Surface state**: `open` (the chat window is up) vs closed (just the FAB
 *    launcher), plus a `sizeClass` (`windowed` floating panel vs `full-height`
 *    docked) persisted under the surface's storage key. `open` is never
 *    persisted — the window starts closed on every load.
 *
 * Surface-specific semantics stay with the providers, built on the exposed
 * `stack`/`setStack`/`setOpen` primitives: the workspace's draft mode and
 * deleted-session eject, the canvas's mint-on-expand and composer model.
 */

import { useLocalStorageState } from "ahooks";
import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

export type FloatingChatSessionHistory = "push" | "replace";

/** Window size class: a small floating panel vs a full-height docked panel. */
export type FloatingChatSizeClass = "windowed" | "full-height";

export interface FloatingChatSessionStack {
  /** Session-id stack, oldest first. Read-only view for surface extras (eject, …). */
  stack: string[];
  /** Top-of-stack session id, or null. */
  currentSessionId: string | null;
  canGoBack: boolean;
  /** True when the chat window is up; false shows only the FAB launcher. */
  open: boolean;
  /** windowed (floating panel) vs full-height (docked). Persisted under the storage key. */
  sizeClass: FloatingChatSizeClass;

  /** Open the window onto a session. Second arg defaults to `"push"`. */
  openSession: (
    sessionId: string,
    history?: FloatingChatSessionHistory,
  ) => void;
  goBack: () => void;
  /** Minimize to the FAB, keeping the session stack so reopening restores context. */
  collapse: () => void;
  /** Close to the FAB and clear session history. */
  closeChat: () => void;
  /** Flip between the windowed and full-height size classes. */
  toggleSizeClass: () => void;

  /** Primitives for surface-specific extras (draft mode, session eject, …). */
  setStack: Dispatch<SetStateAction<string[]>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export function useFloatingChatSessionStack({
  sizeClassStorageKey,
  defaultSizeClass,
}: {
  /** Per-surface localStorage key the size class persists under. */
  sizeClassStorageKey: string;
  defaultSizeClass: FloatingChatSizeClass;
}): FloatingChatSessionStack {
  const [stack, setStack] = useState<string[]>([]);
  // The window opens closed on every load (FAB only); never persisted.
  const [open, setOpen] = useState(false);

  // Size class persists so a user's windowed/full-height preference survives
  // reloads (unlike `open`, which always starts closed).
  const [sizeClassRaw, setSizeClassRaw] =
    useLocalStorageState<FloatingChatSizeClass>(sizeClassStorageKey, {
      defaultValue: defaultSizeClass,
    });
  const sizeClass = sizeClassRaw ?? defaultSizeClass;
  const toggleSizeClass = useCallback(
    () =>
      setSizeClassRaw((prev) =>
        (prev ?? defaultSizeClass) === "windowed" ? "full-height" : "windowed",
      ),
    [setSizeClassRaw, defaultSizeClass],
  );

  const currentSessionId = stack.length > 0 ? stack[stack.length - 1]! : null;
  const canGoBack = stack.length > 1;

  const openSession = useCallback(
    (sessionId: string, history: FloatingChatSessionHistory = "push") => {
      if (!sessionId) return;
      setStack((prev) => {
        if (history === "replace") {
          if (prev.length === 0) return [sessionId];
          return [...prev.slice(0, -1), sessionId];
        }
        if (prev.length === 0) return [sessionId];
        const top = prev[prev.length - 1];
        if (top === sessionId) return prev;
        return [...prev, sessionId];
      });
      // Opening a session always surfaces the window.
      setOpen(true);
    },
    [],
  );

  const goBack = useCallback(() => {
    setStack((prev) => (prev.length <= 1 ? [] : prev.slice(0, -1)));
  }, []);

  // Minimize to the FAB but keep the session stack so reopening restores context.
  const collapse = useCallback(() => setOpen(false), []);

  // Close to the FAB and clear session history.
  const closeChat = useCallback(() => {
    setStack([]);
    setOpen(false);
  }, []);

  return {
    stack,
    currentSessionId,
    canGoBack,
    open,
    sizeClass,
    openSession,
    goBack,
    collapse,
    closeChat,
    toggleSizeClass,
    setStack,
    setOpen,
  };
}
